<script setup lang="ts">
import type { Task } from "@asap/shared/schemas/task/crud";
import TaskAccordion from "~/components/task/TaskAccordion.vue";
import TaskItem from "~/components/task/TaskItem.vue";
import SkeletonTaskItem from "~/components/shared/skeleton/SkeletonTaskItem.vue";
import type { TaskGroupState } from "~/components/task/composables/useTaskGroups";
import { useUserMe } from "~/components/user/composables/useUser";

const TASK_SKELETON_ITEM_COUNT = 1;

defineProps<{
  groups: TaskGroupState[];
  isTaskDisplayedAsDone: (task: Task) => boolean;
}>();

const emit = defineEmits<{
  onGroupOpen: [group: TaskGroupState, isOpen: boolean];
  onTaskDoneChange: [group: TaskGroupState, task: Task, isDone: boolean];
}>();

const { authUser } = useUserMe();

const ui = {
  list: "flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto [scrollbar-gutter:stable]",
  empty: "px-3 py-2 min-h-10 text-sm text-secondary-600",
};
</script>

<template>
  <div :class="ui.list">
    <TaskAccordion
      v-for="group in groups"
      :key="group.id"
      :open="group.isOpen"
      :title="$t(`tasks.page.groups.${group.id}`)"
      :count="group.count"
      :color="group.color"
      @update:open="(isOpen) => emit('onGroupOpen', group, isOpen)"
    >
      <template v-if="group.isLoading">
        <SkeletonTaskItem v-for="skeletonIndex in TASK_SKELETON_ITEM_COUNT" :key="skeletonIndex" />
      </template>
      <TransitionGroup
        v-else
        tag="div"
        class="relative"
        move-class="transition-all duration-200"
        enter-active-class="fade-in duration-300"
        enter-from-class="opacity-0 scale-95"
        enter-to-class="opacity-100 scale-100"
        leave-active-class="absolute w-full fade-in duration-300"
        leave-from-class="opacity-100 scale-100"
        leave-to-class="opacity-0 scale-95"
      >
        <p v-if="group.count === 0" key="empty" :class="ui.empty">{{ $t(`tasks.page.empty.${group.id}`) }}</p>
        <template v-else>
          <TaskItem
            v-for="task in group.items"
            :key="task.id"
            :is-done="isTaskDisplayedAsDone(task)"
            :name="task.name"
            :due-date="task.due_date"
            :action-type="task.action_type"
            :created-at="task.created_at"
            :created-by="task.created_by.auth_user_id === authUser?.id ? null : task.created_by"
            :subjects="task.context.subjects"
            @update:is-done="(isDone) => emit('onTaskDoneChange', group, task, isDone)"
          />
        </template>
      </TransitionGroup>
    </TaskAccordion>
  </div>
</template>
